import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { 
  subscribeToMarketValueChanges, 
  unsubscribeFromChannel,
  fetchRecentMarketValueChanges 
} from '../../../services/realtimeService';

const LiveMarketUpdates = () => {
  const [marketUpdates, setMarketUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch initial data
    loadMarketUpdates();

    // Subscribe to real-time updates
    const channel = subscribeToMarketValueChanges((payload) => {
      handleRealtimeUpdate(payload);
    });

    return () => {
      unsubscribeFromChannel(channel);
    };
  }, []);

  const loadMarketUpdates = async () => {
    setLoading(true);
    try {
      const { data, error: fetchError } = await fetchRecentMarketValueChanges(30);
      if (fetchError) {
        setError(fetchError);
      } else {
        setMarketUpdates(data || []);
      }
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRealtimeUpdate = (payload) => {
    console.log('Real-time market value update:', payload);
    
    // Reload data when new market value is added
    if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE') {
      loadMarketUpdates();
    }
  };

  const formatCurrency = (value, currency = 'EUR') => {
    if (!value) return 'N/A';
    const numValue = parseFloat(value);
    if (numValue >= 1000000) {
      return `${currency} ${(numValue / 1000000)?.toFixed(1)}M`;
    }
    if (numValue >= 1000) {
      return `${currency} ${(numValue / 1000)?.toFixed(0)}K`;
    }
    return `${currency} ${numValue?.toFixed(0)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date?.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="TrendingUp" size={20} color="var(--color-primary)" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Live Market Updates</h3>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card rounded-lg border border-border p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-error/10 flex items-center justify-center">
            <Icon name="AlertCircle" size={20} color="var(--color-error)" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Live Market Updates</h3>
        </div>
        <p className="text-sm text-error">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="TrendingUp" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Live Market Updates</h3>
            <p className="text-xs text-muted-foreground">Real-time player valuations</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
          <span className="text-xs text-success font-medium">LIVE</span>
        </div>
      </div>

      {marketUpdates?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="TrendingUp" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-3 opacity-50" />
          <p className="text-sm text-muted-foreground">No market updates in the last 30 days</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {marketUpdates?.slice(0, 10)?.map((update) => (
            <div 
              key={update?.id} 
              className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
            >
              <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-muted">
                {update?.player?.profilePhoto ? (
                  <Image 
                    src={update?.player?.profilePhoto} 
                    alt={`${update?.player?.fullName || 'Player'} profile photo`}
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-primary/10">
                    <Icon name="User" size={20} color="var(--color-primary)" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-semibold text-foreground truncate">
                  {update?.player?.fullName || update?.player?.knownAs || 'Unknown Player'}
                </h4>
                <p className="text-xs text-muted-foreground truncate">
                  {update?.player?.currentClub || 'No club'} • {update?.player?.primaryPosition || 'N/A'}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs font-medium text-primary">
                    {formatCurrency(update?.marketValue, update?.currency)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(update?.valuationDate)}
                  </span>
                </div>
              </div>
              <div className="flex-shrink-0">
                <Icon name="TrendingUp" size={16} color="var(--color-success)" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default LiveMarketUpdates;